import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { trigger, style, animate, transition, AnimationEvent } from '@angular/animations';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactService } from 'src/app/services/contact.service';
import { TranslateHandler } from 'src/app/common/helpers/translate-handler';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-support-contactus-popup',
  templateUrl: './support-contactus-popup.component.html',
  styleUrls: ['./support-contactus-popup.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1, position: 'fixed', right: 0 }),
        animate('300ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 })),
      ]),
    ]),
  ]

})
export class SupportContactusPopupComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();
  @Input() searchText: string = '';
  @Input() pageKeyword: string = '';

  private readonly CONTACT_MESSAGE_KEY = 'contactMessage';
  private destroy$ = new Subject<void>();
  private suppressSave = false;

  isVisible = true;
  supportForm: FormGroup;
  isContactSuccess = false;
  isLoadingVisible = false;
  apiErrorMessage: string;
  isClosing = false;

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService,
    private authenticationService: AuthenticationService,
    private translateHandler: TranslateHandler,
  ) {
    this.supportForm = this.fb.group({
      message: ['', [Validators.required, Validators.maxLength(1500)]],
    });
  }

  ngOnInit(): void {
    const savedMessage = sessionStorage.getItem(this.CONTACT_MESSAGE_KEY);
    if (savedMessage) {
      this.supportForm.get('message')?.setValue(savedMessage);
    }

    this.supportForm.get('message')?.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(value => {
        if (!this.suppressSave) {
          sessionStorage.setItem(this.CONTACT_MESSAGE_KEY, value || '');
        }
      });
  }


  onAnimationDone(event: AnimationEvent) {
    if (event.toState === 'void') {
      this.isClosing = false;
      this.closed.emit();
    }
  }

  close() {
    if (this.isClosing) return;
    this.isClosing = true;
    this.isVisible = false;
  }

  isValid(controlName: string): boolean {
    const control = this.supportForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onSubmit(): void {
    if (this.supportForm.invalid) {
      this.supportForm.markAllAsTouched();
      return;
    }

    const message = this.supportForm.get('message')?.value;

    this.suppressSave = true;
    this.supportForm.reset();
    sessionStorage.removeItem(this.CONTACT_MESSAGE_KEY);

    this.isLoadingVisible = true;
    this.apiErrorMessage = '';
    this.isContactSuccess = false;
    this.supportForm.disable();

    const user = this.authenticationService.getUser();
    const formData = new FormData();

    formData.append('name', `${user.FirstName} ${user.LastName}`);
    formData.append('email', user.EncryptedEmail);
    formData.append('message', message);
    formData.append('subject', this.searchText);
    formData.append('category', this.pageKeyword);

    this.contactService.submitSupportForm(formData).subscribe({
      next: () => {
        this.isLoadingVisible = false;
        this.isContactSuccess = true;

        this.suppressSave = true;
        this.supportForm.reset();
        sessionStorage.removeItem(this.CONTACT_MESSAGE_KEY);

        setTimeout(() => {
          this.suppressSave = false;
          this.supportForm.enable();
        }, 600);
      },
      error: () => {
        this.isLoadingVisible = false;
        this.apiErrorMessage = this.translateHandler.translate('support_popup.lbl_system_fail');
        this.supportForm.enable();
      },
    });
  }

  onMessageFocus(): void {
    if (this.isContactSuccess) {
      this.isContactSuccess = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

